import React, { useState, useMemo } from 'react';
import { taskAPI } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import { formatDateShort, generateDateArray, getDateRange, getDaysBetween, addDays } from '../lib/utils';
import { ChevronLeft, ChevronRight, Diamond, GripVertical } from 'lucide-react';

export default function GanttChart({ projectId, tasks, milestones, onTaskUpdate, onRefresh }) {
  const [editTask, setEditTask] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [viewOffset, setViewOffset] = useState(0);
  const [dragging, setDragging] = useState(null);

  const dateRange = useMemo(() => getDateRange(tasks), [tasks]);
  const allDates = useMemo(() => generateDateArray(dateRange.start, dateRange.end), [dateRange]);
  
  // Show 30 days at a time
  const visibleDates = useMemo(() => {
    const start = Math.max(0, viewOffset);
    return allDates.slice(start, start + 30);
  }, [allDates, viewOffset]);

  const cellWidth = 40;
  const rowHeight = 48;

  const getTaskPosition = (task) => {
    const startIdx = visibleDates.indexOf(task.start_date);
    const endIdx = visibleDates.indexOf(task.end_date);
    
    if (startIdx === -1 && endIdx === -1) {
      // Task is completely outside visible range
      const taskStart = new Date(task.start_date);
      const visibleStart = new Date(visibleDates[0]);
      const visibleEnd = new Date(visibleDates[visibleDates.length - 1]);
      
      if (taskStart > visibleEnd) return null;
      if (new Date(task.end_date) < visibleStart) return null;
      
      // Partially visible
      const clampedStart = taskStart < visibleStart ? 0 : 
        visibleDates.findIndex(d => new Date(d) >= taskStart);
      const clampedEnd = new Date(task.end_date) > visibleEnd ? 
        visibleDates.length - 1 : visibleDates.findIndex(d => d === task.end_date);
      
      return {
        left: clampedStart * cellWidth,
        width: Math.max((clampedEnd - clampedStart + 1) * cellWidth, cellWidth),
      };
    }
    
    const actualStart = startIdx === -1 ? 0 : startIdx;
    const actualEnd = endIdx === -1 ? visibleDates.length - 1 : endIdx;
    
    return {
      left: actualStart * cellWidth,
      width: Math.max((actualEnd - actualStart + 1) * cellWidth, cellWidth),
    };
  };

  const getMilestonePosition = (milestone) => {
    const idx = visibleDates.indexOf(milestone.date);
    if (idx === -1) return null;
    return { left: idx * cellWidth + cellWidth / 2 };
  };

  const handleDragStart = (e, task) => {
    e.preventDefault();
    setDragging({ task, startX: e.clientX, originalStart: task.start_date, originalEnd: task.end_date });
  };

  const handleDrag = (e) => {
    if (!dragging) return;
    
    const deltaX = e.clientX - dragging.startX;
    const daysDelta = Math.round(deltaX / cellWidth);
    
    if (daysDelta !== 0) {
      const newStart = addDays(dragging.originalStart, daysDelta);
      const newEnd = addDays(dragging.originalEnd, daysDelta);
      
      onTaskUpdate({ ...dragging.task, start_date: newStart, end_date: newEnd });
    }
  };

  const handleDragEnd = async () => {
    if (!dragging) return;
    
    const task = tasks.find(t => t.id === dragging.task.id);
    if (task && (task.start_date !== dragging.originalStart || task.end_date !== dragging.originalEnd)) {
      try {
        await taskAPI.update(projectId, task.id, {
          start_date: task.start_date,
          end_date: task.end_date,
        });
        toast.success('Task updated');
      } catch (error) {
        // Revert on error
        onTaskUpdate({ ...task, start_date: dragging.originalStart, end_date: dragging.originalEnd });
        toast.error('Failed to update task');
      }
    }
    setDragging(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    
    try {
      const res = await taskAPI.update(projectId, editTask.id, {
        name: editTask.name,
        start_date: editTask.start_date,
        end_date: editTask.end_date,
        progress: editTask.progress,
      });
      onTaskUpdate(res.data);
      setEditTask(null);
      toast.success('Task updated');
    } catch (error) {
      toast.error('Failed to update task');
    } finally {
      setEditLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const todayIdx = visibleDates.indexOf(today);

  return (
    <Card className="bg-white border-zinc-200 rounded-xl overflow-hidden" data-testid="gantt-chart">
      {/* Navigation */}
      <div className="flex items-center justify-between p-4 border-b border-zinc-200">
        <h3 className="font-outfit font-semibold text-zinc-900">Gantt Chart - Daily View</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewOffset(Math.max(0, viewOffset - 7))}
            disabled={viewOffset === 0}
            data-testid="gantt-prev-btn"
            className="rounded-lg"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-zinc-600 min-w-[200px] text-center">
            {formatDateShort(visibleDates[0])} - {formatDateShort(visibleDates[visibleDates.length - 1])}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewOffset(Math.min(allDates.length - 30, viewOffset + 7))}
            disabled={viewOffset >= allDates.length - 30}
            data-testid="gantt-next-btn"
            className="rounded-lg"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Chart Area */}
      <div 
        className="overflow-x-auto"
        onMouseMove={handleDrag}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleDragEnd}
      >
        <div style={{ minWidth: visibleDates.length * cellWidth + 250 }}>
          {/* Header - Dates */}
          <div className="flex border-b border-zinc-200 bg-zinc-50">
            <div className="w-[250px] flex-shrink-0 p-3 font-medium text-zinc-700 border-r border-zinc-200">
              Task Name
            </div>
            <div className="flex">
              {visibleDates.map((date, idx) => {
                const d = new Date(date);
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                const isToday = date === today;
                return (
                  <div
                    key={date}
                    className={`flex flex-col items-center justify-center border-r border-zinc-100 text-xs ${
                      isWeekend ? 'bg-zinc-100' : ''
                    } ${isToday ? 'bg-orange-50' : ''}`}
                    style={{ width: cellWidth, height: 50 }}
                  >
                    <span className="text-zinc-500">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                    <span className={`font-medium ${isToday ? 'text-primary' : 'text-zinc-700'}`}>
                      {d.getDate()}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tasks */}
          {tasks.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              No tasks yet. Add tasks from the WBS/Tasks tab.
            </div>
          ) : (
            <div className="relative">
              {/* SVG for dependency arrows - positioned over entire chart */}
              <svg 
                className="absolute top-0 left-[250px] pointer-events-none z-20"
                style={{ 
                  width: visibleDates.length * cellWidth,
                  height: tasks.length * rowHeight + (milestones.length > 0 ? rowHeight : 0)
                }}
              >
                <defs>
                  <marker
                    id="dependency-arrow"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                    markerUnits="strokeWidth"
                  >
                    <path d="M0,0 L0,6 L9,3 z" fill="#94a3b8" />
                  </marker>
                </defs>
                {/* Render all dependency lines */}
                {tasks.map((task, taskIdx) => {
                  const taskPos = getTaskPosition(task);
                  if (!taskPos || !task.dependencies?.length) return null;
                  
                  return task.dependencies.map((depId) => {
                    const depIdx = tasks.findIndex(t => t.id === depId);
                    const depTask = tasks[depIdx];
                    if (!depTask || depIdx === -1) return null;
                    
                    const depPos = getTaskPosition(depTask);
                    if (!depPos) return null;
                    
                    // Calculate coordinates
                    const startX = depPos.left + depPos.width;
                    const startY = depIdx * rowHeight + rowHeight / 2;
                    const endX = taskPos.left;
                    const endY = taskIdx * rowHeight + rowHeight / 2;
                    
                    // Create path with curved connector
                    const midX = startX + (endX - startX) / 2;
                    
                    return (
                      <g key={`${task.id}-${depId}`}>
                        <path
                          d={`M ${startX} ${startY} 
                              C ${midX} ${startY}, ${midX} ${endY}, ${endX - 5} ${endY}`}
                          fill="none"
                          stroke="#94a3b8"
                          strokeWidth="2"
                          strokeDasharray="4,2"
                          markerEnd="url(#dependency-arrow)"
                        />
                        {/* Small circle at start point */}
                        <circle cx={startX} cy={startY} r="3" fill="#94a3b8" />
                      </g>
                    );
                  });
                })}
              </svg>

              {/* Today line */}
              {todayIdx !== -1 && (
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-primary z-10"
                  style={{ left: 250 + todayIdx * cellWidth + cellWidth / 2 }}
                />
              )}

              {tasks.map((task, taskIdx) => {
                const pos = getTaskPosition(task);
                const isSubtask = task.parent_id;
                
                return (
                  <div
                    key={task.id}
                    className="flex border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors"
                    style={{ height: rowHeight }}
                  >
                    <div 
                      className={`w-[250px] flex-shrink-0 flex items-center gap-2 px-3 border-r border-zinc-200 cursor-pointer ${
                        isSubtask ? 'pl-8' : ''
                      }`}
                      onClick={() => setEditTask(task)}
                      data-testid={`task-row-${task.id}`}
                    >
                      <span className="text-sm text-zinc-700 truncate flex-1">{task.name}</span>
                      <span className="text-xs text-zinc-400">{task.progress}%</span>
                    </div>
                    <div className="relative flex-1">
                      {/* Grid lines */}
                      {visibleDates.map((date, idx) => {
                        const d = new Date(date);
                        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                        return (
                          <div
                            key={date}
                            className={`absolute top-0 bottom-0 border-r border-zinc-100 ${
                              isWeekend ? 'bg-zinc-50/50' : ''
                            }`}
                            style={{ left: idx * cellWidth, width: cellWidth }}
                          />
                        );
                      })}
                      
                      {/* Task bar */}
                      {pos && (
                        <div
                          className="absolute top-2 h-8 rounded-full flex items-center group cursor-grab active:cursor-grabbing"
                          style={{
                            left: pos.left,
                            width: pos.width,
                            backgroundColor: task.color || '#f97316',
                          }}
                          onMouseDown={(e) => handleDragStart(e, task)}
                          data-testid={`task-bar-${task.id}`}
                        >
                          {/* Progress fill */}
                          <div
                            className="absolute inset-y-0 left-0 rounded-full bg-black/20"
                            style={{ width: `${task.progress}%` }}
                          />
                          <GripVertical className="w-3 h-3 text-white/60 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                          <span className="text-xs text-white font-medium px-2 truncate relative z-10">
                            {pos.width > 80 ? task.name : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Milestones row */}
              {milestones.length > 0 && (
                <div
                  className="flex border-b border-zinc-100 bg-amber-50/30"
                  style={{ height: rowHeight }}
                >
                  <div className="w-[250px] flex-shrink-0 flex items-center px-3 border-r border-zinc-200">
                    <span className="text-sm font-medium text-amber-700">Milestones</span>
                  </div>
                  <div className="relative flex-1">
                    {milestones.map((milestone) => {
                      const pos = getMilestonePosition(milestone);
                      if (!pos) return null;
                      
                      return (
                        <div
                          key={milestone.id}
                          className="absolute top-1/2 -translate-y-1/2 flex flex-col items-center group"
                          style={{ left: pos.left }}
                          data-testid={`milestone-marker-${milestone.id}`}
                        >
                          <Diamond className="w-5 h-5 text-amber-500 fill-amber-500" />
                          <span className="absolute top-full mt-1 text-xs text-amber-700 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                            {milestone.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Arrow marker definition */}
              <svg className="absolute" style={{ width: 0, height: 0 }}>
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#d4d4d8" />
                  </marker>
                </defs>
              </svg>
            </div>
          )}
        </div>
      </div>

      {/* Edit Task Dialog */}
      <Dialog open={!!editTask} onOpenChange={() => setEditTask(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Edit Task</DialogTitle>
            <DialogDescription>Update task details, dates, and progress</DialogDescription>
          </DialogHeader>
          {editTask && (
            <form onSubmit={handleEditSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Task Name</Label>
                <Input
                  value={editTask.name}
                  onChange={(e) => setEditTask({ ...editTask, name: e.target.value })}
                  data-testid="edit-task-name"
                  className="rounded-xl"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={editTask.start_date}
                    onChange={(e) => setEditTask({ ...editTask, start_date: e.target.value })}
                    data-testid="edit-task-start"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={editTask.end_date}
                    onChange={(e) => setEditTask({ ...editTask, end_date: e.target.value })}
                    data-testid="edit-task-end"
                    className="rounded-xl"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Progress: {editTask.progress}%</Label>
                <Slider
                  value={[editTask.progress]}
                  onValueChange={([val]) => setEditTask({ ...editTask, progress: val })}
                  max={100}
                  step={5}
                  data-testid="edit-task-progress"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditTask(null)} className="rounded-lg">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={editLoading}
                  data-testid="save-task-btn"
                  className="bg-primary hover:bg-orange-600 text-white rounded-lg"
                >
                  {editLoading ? <span className="spinner" /> : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
